import { useEffect, useRef, useState } from "react";

/**
 * Files dropped from the computer onto an area — the same path its «Subir»
 * button takes, so validation, caps and ordering never differ between the two.
 *
 * Reacts ONLY to drags that carry files. Reordering gallery photos is a
 * different mechanism (dnd-kit's pointer events), and must never light a zone
 * up. The highlight counts enters and leaves instead of turning off on the
 * first `dragleave`, which fires every time the pointer crosses one of the
 * zone's own children and made the border flicker.
 */
export function useFileDropZone({
  onFiles,
  disabled = false,
}: {
  onFiles: (files: FileList) => void;
  disabled?: boolean;
}): {
  isOver: boolean;
  zoneProps: {
    onDragEnter: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
  };
} {
  const [isOver, setIsOver] = useState(false);
  const depth = useRef(0);

  return {
    isOver,
    zoneProps: {
      onDragEnter: (e) => {
        if (!carriesFiles(e.dataTransfer)) return;
        e.preventDefault();
        depth.current += 1;
        setIsOver(true);
      },
      onDragOver: (e) => {
        if (!carriesFiles(e.dataTransfer)) return;
        // Without this the browser refuses the drop.
        e.preventDefault();
        e.dataTransfer.dropEffect = disabled ? "none" : "copy";
      },
      onDragLeave: (e) => {
        if (!carriesFiles(e.dataTransfer)) return;
        depth.current = Math.max(0, depth.current - 1);
        if (depth.current === 0) setIsOver(false);
      },
      onDrop: (e) => {
        if (!carriesFiles(e.dataTransfer)) return;
        e.preventDefault();
        e.stopPropagation();
        depth.current = 0;
        setIsOver(false);
        if (!disabled && e.dataTransfer.files.length > 0) onFiles(e.dataTransfer.files);
      },
    },
  };
}

/**
 * While mounted, a file dropped OUTSIDE every zone does nothing — instead of
 * the browser's default, which opens the file in the tab and throws the admin
 * out of the CRM.
 */
export function useBlockStrayFileDrops(): void {
  useEffect(() => {
    function block(e: DragEvent): void {
      if (carriesFiles(e.dataTransfer)) e.preventDefault();
    }
    window.addEventListener("dragover", block);
    window.addEventListener("drop", block);
    return () => {
      window.removeEventListener("dragover", block);
      window.removeEventListener("drop", block);
    };
  }, []);
}

function carriesFiles(dataTransfer: DataTransfer | null): boolean {
  return Array.from(dataTransfer?.types ?? []).includes("Files");
}
